/**
 * Entrance animations for sections, cards and hero content.
 *
 * The hidden starting state is applied by CSS in `snippets/scroll-reveal.liquid`
 * so it is in place before first paint. This file only decides *when* to add
 * `.reveal-in`. The target selectors below therefore have to stay in sync with
 * the selectors in that snippet.
 *
 * Elements entering in the same observer callback are staggered in reading
 * order, so a later grid row restarts the cascade rather than inheriting the
 * delay of the rows above it.
 *
 * Priming is tracked per-element (`data-reveal-primed`), not per-section.
 * Several storefront components (the collection grid's `<results-list>`,
 * product recommendations) render their cards asynchronously, after the
 * initial section scan already ran and found none. A MutationObserver keeps
 * watching `#MainContent` for the rest of the page's life and primes any
 * matching element the moment it actually appears, however late.
 */

const STAGGER_STEP_MS = 80;
const MAX_STAGGER_STEPS = 6;

const HERO_CONTENT = '.hero__content-wrapper';
const CARD = '.resource-list__item, .category-circle, .product-grid__item';
/*
 * Two different wrappers, because section types disagree on where the header
 * lives: collection-list treats it as a regular editable block rendered into
 * `__content`; product-list renders a fixed header into `__header` and
 * reserves `__content` for optional merchant-added blocks.
 */
const LIST_HEADER = '.section-resource-list__content, .section-resource-list__header, .category-circles__header';

/** @type {IntersectionObserver | undefined} */
let observer;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {Element} element
 * @param {number} index
 */
function reveal(element, index) {
  if (!(element instanceof HTMLElement)) return;

  const step = Math.min(index, MAX_STAGGER_STEPS);
  if (step > 0) element.style.setProperty('--reveal-delay', `${step * STAGGER_STEP_MS}ms`);
  element.classList.add('reveal-in');
}

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  const visible = entries.filter((entry) => entry.isIntersecting);
  if (!visible.length) return;

  // Cascade in reading order rather than observer-registration order.
  visible.sort((a, b) => {
    const rowDelta = a.boundingClientRect.top - b.boundingClientRect.top;
    if (Math.abs(rowDelta) > 4) return rowDelta;
    return a.boundingClientRect.left - b.boundingClientRect.left;
  });

  visible.forEach((entry, index) => {
    observer?.unobserve(entry.target);
    reveal(entry.target, index);
  });
}

/**
 * Marks an element as seen so it is never queued twice, then hands
 * still-hidden elements to the IntersectionObserver.
 *
 * @param {Element} element
 */
function primeAndObserve(element) {
  if (!(element instanceof HTMLElement)) return;
  if (element.dataset.revealPrimed === 'true') return;
  element.dataset.revealPrimed = 'true';

  if (element.classList.contains('reveal-in')) return;
  observer?.observe(element);
}

/**
 * Finds every match for `selector` at or below `root`, root included.
 *
 * @param {Element} root
 * @param {string} selector
 * @returns {Element[]}
 */
function queryIncludingSelf(root, selector) {
  const matches = root.matches(selector) ? [root] : [];
  matches.push(...root.querySelectorAll(selector));
  return matches;
}

/**
 * Catches cards and list headers that render asynchronously, after the
 * initial per-section scan already ran. Safe to call repeatedly and on
 * arbitrary subtrees: already-primed elements are skipped.
 *
 * @param {Element} root
 */
function primeStragglers(root) {
  queryIncludingSelf(root, CARD).forEach(primeAndObserve);

  queryIncludingSelf(root, LIST_HEADER).forEach((header) => {
    // Only a target when its own section actually has cards, matching the
    // CSS rule that gates the header's hidden starting state.
    if (header.closest('.shopify-section')?.querySelector(CARD)) primeAndObserve(header);
  });
}

/**
 * Picks what animates inside a section, mirroring the CSS selectors.
 *
 * @param {HTMLElement} section
 * @returns {{ onLoad: boolean, items: Element[] }}
 */
function collectTargets(section) {
  // The hero is above the fold and is the LCP candidate, so its content is
  // revealed on load rather than on scroll. The hero image is never a target,
  // so its paint is not gated behind an opacity transition.
  const heroContent = section.querySelector(HERO_CONTENT);
  if (heroContent) return { onLoad: true, items: Array.from(heroContent.children) };

  // Card grids animate per card so the grid cascades.
  const cards = Array.from(section.querySelectorAll(CARD));
  if (cards.length) {
    const header = section.querySelector(LIST_HEADER);
    return { onLoad: false, items: header ? [header, ...cards] : cards };
  }

  return { onLoad: false, items: [section] };
}

/** @param {Element} section */
function scanSection(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.dataset.revealScanned === 'true') return;
  section.dataset.revealScanned = 'true';

  const { onLoad, items } = collectTargets(section);

  if (onLoad) {
    const pending = items.filter((item) => !item.classList.contains('reveal-in'));
    if (!pending.length) return;
    // Two frames: the hidden state has to be committed before the class flips,
    // otherwise the browser collapses both into one style resolution and no
    // transition runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => pending.forEach(reveal));
    });
    return;
  }

  items.forEach(primeAndObserve);
}

function init() {
  const main = document.querySelector('#MainContent');
  if (!main || prefersReducedMotion()) return;

  observer = new IntersectionObserver(onIntersect, {
    // Hold the reveal until the element is a little way into the viewport.
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.01,
  });

  main.querySelectorAll(':scope > .shopify-section').forEach(scanSection);

  // Sections re-render as fresh nodes in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) scanSection(event.target);
  });

  // Some components (the collection grid, product recommendations) hydrate
  // their cards asynchronously, well after the scan above already ran and
  // found nothing. Keep watching for the rest of the page's life.
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) primeStragglers(node);
      }
    }
  });
  mutationObserver.observe(main, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
